import { useState } from 'react'
import { AppIcon } from '@/shared/ui/icons'

import { DateTime } from 'luxon'
import VerificationInput from 'react-verification-input'

import { Button, Card } from '@/shared/ui'

import { BottomSheetWrapper } from '../BottomSheetWrapper'
import { useAccountDeletion } from './useAccountDeletion'

export const AccountDeletion = ({ user }) => {
  const {
    state,
    isPending,
    isScheduled,
    isRequesting,
    isConfirming,
    isCancelling,
    requestDeletion,
    confirmDeletion,
    cancelDeletion,
  } = useAccountDeletion(user)

  const [openTokenSheet, setOpenTokenSheet] = useState(false)
  const [token, setToken] = useState('')
  const [tokenError, setTokenError] = useState(null)

  const sentAt = state.tokenSentAt || state.requestedAt
  const formattedSentAt = sentAt
    ? DateTime.fromISO(sentAt).toFormat("dd/MM/yyyy 'às' HH:mm")
    : null

  const scheduledFor = state.confirmedAt
    ? DateTime.fromISO(state.confirmedAt).plus({ days: 30 }).toFormat('dd/MM/yyyy')
    : null

  const handleOpenTokenSheet = () => {
    setToken('')
    setTokenError(null)
    setOpenTokenSheet(true)
  }

  const handleConfirm = async () => {
    const trimmed = token.trim()
    if (!trimmed || trimmed.length < 6) return

    try {
      setTokenError(null)
      await confirmDeletion(trimmed)
      setOpenTokenSheet(false)
      setToken('')
    } catch (error) {
      setTokenError(
        error?.response?.data?.error_description ||
        error?.response?.data?.error ||
        'Token inválido ou expirado. Gere um novo e tente novamente.'
      )
    }
  }

  return (
    <>
      <Card className="mt-6">
        <Card.Header className="border-b border-gray-100 px-5 py-4">
          <h3 className="text-lg font-bold text-gray-900">Exclusão de conta</h3>
        </Card.Header>
        <Card.Body className="pb-0">
          <p className="text-gray-700">
            Ao solicitar a exclusão, enviaremos um token por e-mail para confirmar o processo.
            Depois da confirmação você terá até 30 dias para cancelar antes que todos os seus
            acessos sejam revogados.
          </p>
          <div className="mt-4 flex items-start gap-4 rounded-lg border border-dashed border-gray-200 bg-gray-50 p-4">
            <AppIcon name="shield-check" className="shrink-0 text-2xl text-brand" />
            <div className="text-gray-700">
              <strong>Importante:</strong>{' '}
              Depois do prazo de 30 dias, seus dados serão removidos definitivamente de acordo
              com a LGPD.
            </div>
          </div>
          {isPending && (
            <div className="mt-4 flex flex-col gap-2 rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900">
              <div className="font-bold">Solicitação enviada</div>
              <div>
                Enviamos o e-mail com o token
                {formattedSentAt ? ` em ${formattedSentAt}.` : ' recentemente.'}{' '}
                Caso não tenha recebido, verifique a caixa de spam ou clique em &quot;Reenviar e-mail&quot;.
              </div>
            </div>
          )}
          {isScheduled && (
            <div className="mt-4 flex flex-col gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-red-900">
              <div className="font-bold">Exclusão confirmada</div>
              <div>
                Sua conta será excluída
                {scheduledFor ? ` até ${scheduledFor}` : ' em até 30 dias'}
                , salvo se você cancelar o processo antes do prazo.
              </div>
            </div>
          )}
        </Card.Body>
        <Card.Footer className="flex flex-wrap gap-3 border-t border-gray-100 px-5 py-4">
          {state.status === 'idle' && (
            <Button
              type="button"
              variant="danger"
              className="border-dashed bg-transparent"
              onClick={requestDeletion}
              loading={isRequesting}
              showTrailingIcon={false}
            >
              {isRequesting ? 'Solicitando...' : 'Excluir conta'}
            </Button>
          )}
          {isPending && (
            <>
              <Button type="button" variant="primary" onClick={handleOpenTokenSheet}>
                Inserir token de confirmação
              </Button>
              <Button
                type="button"
                variant="light"
                onClick={requestDeletion}
                loading={isRequesting}
                showTrailingIcon={false}
              >
                {isRequesting ? 'Reenviando...' : 'Reenviar e-mail'}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={cancelDeletion}
                loading={isCancelling}
                showTrailingIcon={false}
              >
                {isCancelling ? 'Cancelando...' : 'Cancelar solicitação'}
              </Button>
            </>
          )}
          {isScheduled && (
            <Button
              type="button"
              variant="primary"
              onClick={cancelDeletion}
              loading={isCancelling}
              showTrailingIcon={false}
            >
              {isCancelling ? 'Cancelando...' : 'Cancelar exclusão'}
            </Button>
          )}
        </Card.Footer>
      </Card>

      <BottomSheetWrapper
        open={openTokenSheet}
        onClose={() => setOpenTokenSheet(false)}
        title="Inserir token de confirmação"
      >
        <p className="mb-4 text-gray-700">
          Confirmamos o envio do código de confirmação para o seu e-mail. Ele é válido por 1
          hora e deve ser mantido em sigilo.
        </p>
        <div className="flex justify-center">
          <VerificationInput
            value={token}
            onChange={setToken}
            autoFocus
            length={6}
            placeholder=""
            classNames={{
              container: 'mb-4 flex min-w-[280px] flex-wrap justify-center gap-2',
              character:
                'm-0 h-[55px] w-[55px] rounded-[0.625rem] border border-gray-300 bg-white text-center text-2xl font-bold focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand',
              characterInactive: 'text-gray-700',
              characterSelected: 'border-brand',
            }}
            inputProps={{
              inputMode: 'text',
              autoComplete: 'one-time-code',
              maxLength: 6,
            }}
          />
        </div>
        {tokenError && (
          <div className="mt-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            {tokenError}
          </div>
        )}
        <div className="mt-4 flex gap-3">
          <Button
            type="button"
            variant="danger"
            size="sm"
            disabled={token.trim().length < 6}
            loading={isConfirming}
            showTrailingIcon={false}
            onClick={handleConfirm}
          >
            {isConfirming ? 'Confirmando...' : 'Confirmar exclusão'}
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setOpenTokenSheet(false)}
            disabled={isConfirming}
          >
            Cancelar
          </Button>
        </div>
      </BottomSheetWrapper>
    </>
  )
}
